/**
 * Dialog for creating Cost Sheet from Google Sheet template
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { FileSpreadsheet, Loader2, CheckCircle2, XCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Progress } from '@/components/ui/progress';
import { useQueryClient } from '@tanstack/react-query';

interface CostSheetResponse {
  success: boolean;
  projectId: string;
  projectName: string;
  costSheet?: {
    id: string;
    name: string;
    url: string;
  };
  executionTime: string;
  tracingId: string;
  error?: {
    code: string;
    message: string;
  };
}

interface CreateCostSheetDialogProps {
  project: {
    id: string;
    project_name: string;
    new_cost_folder?: string | null;
    cost_file?: string | null;
  };
  isOpen: boolean;
  onClose: () => void;
}

export function CreateCostSheetDialog({ 
  project, 
  isOpen, 
  onClose 
}: CreateCostSheetDialogProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [result, setResult] = useState<CostSheetResponse | null>(null);
  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false);
  const queryClient = useQueryClient();

  const hasCostFolder = !!project.new_cost_folder;
  const hasExistingCostFile = !!project.cost_file;

  const executeCreateSheet = async () => {
    setShowOverwriteDialog(false);
    setIsCreating(true);
    setProgress(0);
    setResult(null);
    setStatusMessage('Initializing...');

    try {
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 15, 90));
      }, 800);

      setStatusMessage('Authenticating with Google Drive...');
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setStatusMessage('Copying template sheet...');

      const { data, error } = await supabase.functions.invoke('create-cost-sheet', {
        body: { projectId: project.id }
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (error) {
        throw new Error(error.message || 'Unknown error occurred');
      }

      if (!data) {
        throw new Error('No response from server');
      }

      setResult(data);
      setStatusMessage('Completed!');

      if (data.success) {
        toast.success('Cost Sheet created successfully!', {
          description: `Created in ${data.executionTime}`
        });
        
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['project-detail', project.id] });
      } else {
        throw new Error(data.error?.message || 'Operation failed');
      }

    } catch (error: any) {
      console.error('Cost sheet creation error:', error);
      setProgress(0);
      setStatusMessage('');
      
      toast.error('Failed to create cost sheet', {
        description: error.message
      });

      setResult({
        success: false,
        projectId: project.id,
        projectName: project.project_name,
        executionTime: '0ms',
        tracingId: 'N/A',
        error: {
          code: 'UNKNOWN_ERROR',
          message: error.message
        }
      });

    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateSheet = async () => {
    if (!hasCostFolder) {
      toast.error('No cost folder available', {
        description: 'Please create GDrive folders first'
      });
      return;
    }

    // Show warning if cost_file already exists
    if (hasExistingCostFile) {
      setShowOverwriteDialog(true);
      return;
    }

    await executeCreateSheet();
  };

  const handleClose = () => {
    if (!isCreating) {
      onClose();
      setTimeout(() => {
        setProgress(0);
        setStatusMessage('');
        setResult(null);
      }, 300);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Cost Sheet</DialogTitle>
          <DialogDescription>
            Project: <strong>{project.project_name}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Info Box */}
          <div className="rounded-lg border bg-muted/50 p-3">
            <p className="text-sm text-muted-foreground">
              This will create a Cost Sheet from template and save it to the project's _COST folder.
            </p>
          </div>

          {/* No cost folder warning */}
          {!hasCostFolder && (
            <div className="flex items-start gap-2 rounded-lg border border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950 p-3">
              <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Project does not have a Cost folder. Please create GDrive folders first using the "NEW GDrive" button.
              </p>
            </div>
          )}

          {/* Progress Bar */}
          {isCreating && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{statusMessage}</span>
                <span className="font-medium">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
          )}

          {/* Result Display */}
          {result && (
            <div className={`rounded-lg border p-4 space-y-2 ${
              result.success 
                ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950' 
                : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950'
            }`}>
              <div className="flex items-start gap-2">
                {result.success ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                )}
                <div className="flex-1 space-y-1">
                  <p className="font-medium text-sm">
                    {result.success ? 'Success!' : 'Error'}
                  </p>
                  {result.success && result.costSheet && (
                    <>
                      <p className="text-sm text-muted-foreground">
                        Created <strong>{result.costSheet.name}</strong>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Execution time: {result.executionTime}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        asChild
                      >
                        <a href={result.costSheet.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                          Open Cost Sheet
                        </a>
                      </Button>
                    </>
                  )}
                  {!result.success && result.error && (
                    <>
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {result.error.message}
                      </p>
                      {result.tracingId && result.tracingId !== 'N/A' && (
                        <p className="text-xs text-muted-foreground font-mono">
                          Trace ID: {result.tracingId}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isCreating}
          >
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              onClick={handleCreateSheet}
              disabled={isCreating || !hasCostFolder}
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Create Sheet
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Overwrite Warning Dialog */}
      <AlertDialog open={showOverwriteDialog} onOpenChange={setShowOverwriteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              Cost Sheet Already Exists
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-left">
              <p>
                This project already has a Cost Sheet linked. Creating a new one will overwrite the existing link.
              </p>
              <p className="text-foreground font-medium">
                Do you want to continue?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCreating}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={executeCreateSheet}
              disabled={isCreating}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              Overwrite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
